import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";
import {
  Paper,
  TextField,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import CustomButton from "../Button/CustomButton";
import styles from "./ChangePassword.module.scss";
import { useAuth, roles } from "../../services/auth";
import { changePasswordSchema } from "../../services/formValidation";
import { useUpdateUserPassword } from "../../utils/react-query/queryEcommerce/queries";

type ChangePasswordFormData = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

const ALLOWED_ROLES = [
  roles.client,
  roles.supplier,
  roles.lsh,
  roles.salesperson,
];

const ChangePassword: React.FC = () => {
  const { currentRoleId } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { mutateAsync: updatePassword, isLoading } = useUpdateUserPassword();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormData>({
    resolver: yupResolver(changePasswordSchema),
  });

  // Don't show component if role is not allowed
  if (!ALLOWED_ROLES.includes(currentRoleId)) {
    return null;
  }

  const onSubmit = async (data: ChangePasswordFormData) => {
    const result = await updatePassword({
      current_password: data.currentPassword,
      new_password: data.newPassword,
      confirm_password: data.confirmPassword,
    });

    // Only reset form if password was updated successfully
    if (result) {
      reset();
    }
  };

  return (
    <Paper className={styles.container}>
      <h2 className={styles.title}>Cambiar Contraseña</h2>
      <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
        <TextField
          label="Contraseña Actual"
          type={showCurrentPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          inputRef={register}
          name="currentPassword"
          error={!!errors.currentPassword}
          helperText={errors.currentPassword?.message}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  edge="end"
                >
                  {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Nueva Contraseña"
          type={showNewPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          inputRef={register}
          name="newPassword"
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  edge="end"
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Confirmar Nueva Contraseña"
          type={showConfirmPassword ? "text" : "password"}
          variant="outlined"
          fullWidth
          inputRef={register}
          name="confirmPassword"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  edge="end"
                >
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <div className={styles.buttonContainer}>
          <CustomButton
            color="secondary"
            shape="little-rounded"
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
          >
            Cambiar Contraseña
          </CustomButton>
        </div>
      </form>
    </Paper>
  );
};

export default ChangePassword;
